import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { appendReportAuditEntry } from './reportAuditHelpers';

/**
 * Regenerate ALL AI outputs for the Semi-Structured Interview stage.
 *
 * This mirrors TriggerRegenerateCIStage but targets the SSI module. It is
 * used when patient form data (e.g. BAARS-IV rating scales) has changed after
 * the original SSI outputs were generated - for instance when a patient's
 * forms were unlocked post-assessment so they could complete outstanding
 * questionnaires.
 *
 * Behaviour:
 * - Clears all existing paragraph approvals so every paragraph returns to
 *   "needs review" (purple) state — the review page (RD_SSI) determines
 *   review status by checking which paragraph codes exist in
 *   `approvedParagraphs`; without clearing this map, paragraphs whose AI
 *   output didn't change would incorrectly remain approved.
 * - Sets ssiReportProcessingStatus to 'processing' before starting
 * - Calls GenerateDraftWithLLM for each provided SSI section title with
 *   force_regenerate_keys: ['__ALL__'] so the backend regenerates all
 *   paragraphs regardless of previously stored inputs/edits
 * - Waits for all generations to complete (no fire-and-forget)
 * - Sets processing status to 'complete' on success or 'error' on failure
 */
export const TriggerRegenerateSSIStage = async ({ patientId, clinicianId, sectionTitles }) => {
  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!patientId || !clinicianId || !Array.isArray(sectionTitles) || sectionTitles.length === 0) {
    return { success: false, error: 'Missing or invalid parameters' };
  }

  const patientDocRef = doc(db, 'patients', patientId);
  const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM');

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const callWithRetry = async (params, maxRetries = 2) => {
    let attempt = 0;
    await delay(400);
    while (attempt <= maxRetries) {
      try {
        const res = await GenerateDraftWithLLM(params);
        return res;
      } catch (err) {
        if (err?.code === 'deadline-exceeded') {
          throw err;
        }
        if (attempt === maxRetries) throw err;
        await delay(1000 * (attempt + 1));
        attempt += 1;
      }
    }
  };

  try {
    // Flag as processing and wipe all prior approvals so every paragraph
    // returns to "needs review" once the clinician opens the review page.
    // SSI uses MODULE_NUMBER = 2 in the review document components.
    await updateDoc(patientDocRef, {
      'dataModel.6_manageReports.ssiReportProcessingStatus': 'processing',
      'dataModel.6_manageReports.ssiReportProcessingStartedAt': serverTimestamp(),
      'dataModel.6_manageReports.sections.2.approvedParagraphs': {},
      'dataModel.6_manageReports.ssiReportApproved': false,
      'dataModel.6_manageReports.ssiReportApprovedBy': null,
      'dataModel.6_manageReports.ssiReportApprovedByName': null,
      'dataModel.6_manageReports.ssiReportApprovedAt': null,
    });

    appendReportAuditEntry(patientId, {
      module: 'ssi',
      eventType: 'approvalReset',
      isFirstOccurrence: false,
    });

    for (const sectionTitle of sectionTitles) {
      await callWithRetry({
        clinicianId,
        patientId,
        reportStage: 'Semi-Structured Interview',
        reportSection: sectionTitle,
        isTest: false,
        force_regenerate_keys: ['__ALL__']
      });
    }

    await updateDoc(patientDocRef, {
      'dataModel.6_manageReports.ssiReportProcessingStatus': 'complete',
      'dataModel.6_manageReports.ssiReportGenerated': true
    });

    return { success: true };
  } catch (error) {
    try {
      await updateDoc(patientDocRef, {
        'dataModel.6_manageReports.ssiReportProcessingStatus': 'error'
      });
    } catch (_) {}
    return { success: false, error };
  }
};
